import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { calcDatePreset } from '../../components/fields/DateRangePicker';
import DateRangeSelect from '../../components/fields/DateRangeSelect';
import PaginationComponent from '../../components/PaginationComponent';
import { qcResultAPI } from '../../services';
import { SOURCE_KIND_LABELS } from './QcResultsListTab';

const PAGE_SIZE = 20;

const STATUS_LABEL = { PASSED: 'Đạt', FAILED: 'Không đạt' };
const REASON_LABEL = { RANDOM: 'Bốc ngẫu nhiên', FLAGGED_FAST: 'Phiên bị cờ chấm nhanh' };

/**
 * #175146 — Tab "Hậu kiểm": danh sách mục đã chấm được bốc vào diện hậu kiểm (~20% + ưu tiên phiên
 * bị cờ chấm nhanh). Quản lý/ADMIN chấm lại; chấm LỆCH với người chấm đầu thì ghi nhận, lệch đủ
 * số lần trong tháng thì hệ thống tạo điểm phạt cho người chấm (theo khuôn phạt đang có).
 */
export default function QcAuditsTab({ branchId, employeeId, onOpenResult, onClearEmployee }) {
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [audited, setAudited] = useState('false');
  const [onlyMismatch, setOnlyMismatch] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });

  const [target, setTarget] = useState(null);
  const [auditStatus, setAuditStatus] = useState('PASSED');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await qcResultAPI.getQcAudits({
        branch_id: branchId || undefined,
        qc_employee_id: employeeId || undefined,
        audited,
        is_mismatch: onlyMismatch ? 'true' : undefined,
        from: range.from,
        to: range.to,
        page,
        limit: PAGE_SIZE,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta || { total: 0 });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được danh sách hậu kiểm');
    } finally {
      setLoading(false);
    }
  }, [branchId, employeeId, audited, onlyMismatch, range, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [branchId, employeeId, audited, onlyMismatch, range]);

  const openReview = (row) => {
    setTarget(row);
    setAuditStatus(row.original_status);
    setNote('');
  };

  const submitReview = async () => {
    if (!target) return;
    try {
      setSaving(true);
      await qcResultAPI.reviewQcAudit(target.id, { audit_status: auditStatus, note: note || undefined });
      toast.success(
        auditStatus === target.original_status
          ? 'Đã ghi nhận hậu kiểm (khớp kết quả gốc).'
          : 'Đã ghi nhận LỆCH — hệ thống xử lý phạt theo quy định nếu đủ số lần.',
      );
      setTarget(null);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không chấm được mục hậu kiểm');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <DateRangeSelect
                fromDate={range.from}
                toDate={range.to}
                onChange={(from, to) => setRange({ from, to })}
                defaultPreset="last3months"
              />
            </Col>
            <Col md={3}>
              <Form.Label>Trạng thái hậu kiểm</Form.Label>
              <Form.Select value={audited} onChange={(e) => setAudited(e.target.value)}>
                <option value="false">Chưa hậu kiểm</option>
                <option value="true">Đã hậu kiểm</option>
                <option value="">Tất cả</option>
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Check
                type="switch"
                id="qc-audit-only-mismatch"
                checked={onlyMismatch}
                onChange={(e) => setOnlyMismatch(e.target.checked)}
                label="Chỉ hiện mục bị chấm LỆCH"
              />
            </Col>
          </Row>
          {employeeId && (
            <div className="mt-2 d-flex align-items-center gap-2">
              <span className="text-muted small">Đang lọc theo 1 người chấm.</span>
              <Button size="sm" variant="outline-secondary" onClick={onClearEmployee}>
                Bỏ lọc
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có mục hậu kiểm theo bộ lọc hiện tại.</div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Ngày bốc</th>
                  <th>Người chấm</th>
                  <th>Nguồn</th>
                  <th>Kết quả gốc</th>
                  <th>Lý do bốc</th>
                  <th>Trạng thái</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={r.is_mismatch ? 'table-danger' : ''}>
                    <td>{r.selected_at ? new Date(r.selected_at).toLocaleString('vi-VN') : '—'}</td>
                    <td>{r.qc_employee_name || r.qc_employee_id}</td>
                    <td>{SOURCE_KIND_LABELS[r.source_kind] || r.source_kind}</td>
                    <td>
                      <Badge bg={r.original_status === 'PASSED' ? 'success' : 'danger'}>
                        {STATUS_LABEL[r.original_status] || r.original_status}
                      </Badge>
                    </td>
                    <td>{REASON_LABEL[r.selected_reason] || r.selected_reason}</td>
                    <td>
                      {r.audited_at ? (
                        <Badge bg={r.is_mismatch ? 'danger' : 'secondary'}>
                          {r.is_mismatch ? 'LỆCH' : 'Khớp'}
                        </Badge>
                      ) : (
                        <Badge bg="warning" text="dark">Chờ hậu kiểm</Badge>
                      )}
                    </td>
                    <td className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => onOpenResult && onOpenResult(r.source_kind, r.source_id)}
                      >
                        Xem bằng chứng
                      </Button>
                      {!r.audited_at && (
                        <Button size="sm" variant="outline-primary" onClick={() => openReview(r)}>
                          Chấm lại
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <PaginationComponent
            totalItems={meta.total || 0}
            itemsPerPage={PAGE_SIZE}
            currentPage={page}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}

      <Modal show={!!target} onHide={() => setTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Chấm lại mục hậu kiểm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target && (
            <>
              <p className="mb-2">
                Người chấm: <strong>{target.qc_employee_name || target.qc_employee_id}</strong> — kết quả
                gốc:{' '}
                <Badge bg={target.original_status === 'PASSED' ? 'success' : 'danger'}>
                  {STATUS_LABEL[target.original_status] || target.original_status}
                </Badge>
              </p>
              <Form.Group className="mb-2">
                <Form.Label>Kết quả chấm lại</Form.Label>
                <Form.Select value={auditStatus} onChange={(e) => setAuditStatus(e.target.value)}>
                  <option value="PASSED">Đạt</option>
                  <option value="FAILED">Không đạt</option>
                </Form.Select>
              </Form.Group>
              {auditStatus !== target.original_status && (
                <Form.Group>
                  <Form.Label>Ghi chú lệch (tuỳ chọn)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Vì sao kết quả chấm lại khác người chấm đầu…"
                  />
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTarget(null)} disabled={saving}>
            Huỷ
          </Button>
          <Button variant="primary" onClick={submitReview} disabled={saving}>
            {saving ? 'Đang lưu…' : 'Xác nhận'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
